import React from "react";
import StepperForm, { Step } from "../../shared/components/StepperForm";
import { FieldType } from "../../shared/interfaces/FieldType";
import { FieldConfig } from "../../shared/components/DynamicForm";
import { useClient } from "../../context/ClientContext";
import { useStepStatus } from "../../shared/hooks/useStepStatus";

// ────────────────────────────────────────────────
// Step 1: Particulars
// ────────────────────────────────────────────────
const particularsFields: FieldConfig[] = [
  {
    key: "id",
    label: "",
    type: FieldType.INPUT,
    inputType: "hidden",
    size: 12,
  },
  {
    key: "csrf",
    label: "",
    type: FieldType.INPUT,
    inputType: "hidden",
    size: 12,
  },
  {
    key: "photo_path",
    label: "Profile Picture",
    type: FieldType.INPUT,
    inputType: "file",
    placeholder: "Attach profile picture (max 10MB, png/jpg/jpeg)",
    validations: { required: true },
    size: 6,
  },
  {
    key: "first_name",
    label: "First Name",
    type: FieldType.INPUT,
    placeholder: "Enter First Name",
    validations: { required: true },
    size: 6,
  },
  {
    key: "middle_name",
    label: "Middle Name",
    type: FieldType.INPUT,
    placeholder: "Enter Middle Name",
    size: 6,
  },
  {
    key: "surname",
    label: "Surname",
    type: FieldType.INPUT,
    placeholder: "Enter Surname",
    validations: { required: true },
    size: 6,
  },
  {
    key: "gender_id",
    label: "Gender",
    type: FieldType.SELECT,
    inputType: "number",
    options: [
      { value: "1", label: "Male" },
      { value: "2", label: "Female" },
    ],
    validations: { required: true },
    size: 6,
  },
  {
    key: "name_prefix_id",
    label: "Name Prefix",
    type: FieldType.SELECT,
    inputType: "number",
    options: [
      { value: "1", label: "Mr" },
      { value: "2", label: "Mrs" },
      { value: "3", label: "Ms" },
      { value: "4", label: "Dr" },
    ],
    validations: { required: true },
    size: 6,
  },
  {
    key: "nida_number",
    label: "NIDA Number",
    type: FieldType.INPUT,
    placeholder: "Enter NIDA Number",
    validations: { required: true },
    size: 6,
  },
  {
    key: "dob",
    label: "Date of Birth",
    type: FieldType.INPUT,
    inputType: "date",
    validations: { required: true },
    size: 6,
  },
  {
    key: "country_code_id",
    label: "Country Code",
    type: FieldType.SELECT,
    inputType: "number",
    options: [
      { value: "1", label: "+1" },
      { value: "255", label: "+255" },
    ],
    validations: { required: true },
    size: 6,
  },
  {
    key: "phone_number",
    label: "Phone Number",
    type: FieldType.INPUT,
    placeholder: "Enter Phone Number",
    validations: { required: true },
    size: 6,
  },
];

// ────────────────────────────────────────────────
// Step 2: Passport
// ────────────────────────────────────────────────
const passportFields: FieldConfig[] = [
  {
    key: "passport_number",
    label: "Passport Number",
    type: FieldType.INPUT,
    inputType: "number",
    placeholder: "Enter Passport Number",
    validations: { required: true },
    size: 6,
  },
  {
    key: "passport_issue_date",
    label: "Issue Date",
    type: FieldType.INPUT,
    inputType: "date",
    validations: { required: true },
    size: 6,
  },
  {
    key: "passport_expiry_date",
    label: "Expiry Date",
    type: FieldType.INPUT,
    inputType: "date",
    validations: { required: true },
    size: 6,
  },
  {
    key: "passport_country",
    label: "Country of Issue",
    type: FieldType.SELECT,
    inputType: "number",
    options: [
      { value: 1, label: "Tanzania" },
      { value: 2, label: "USA" },
      { value: 3, label: "UK" },
    ],
    validations: { required: true },
    size: 6,
  },
];

// ────────────────────────────────────────────────
// Step 3: Education
// ────────────────────────────────────────────────
const educationFields: FieldConfig[] = [
  {
    key: "highest_qualification",
    label: "Highest Qualification",
    type: FieldType.SELECT,
    options: [
      { value: "high_school", label: "High School" },
      { value: "bachelor", label: "Bachelor's" },
      { value: "master", label: "Master's" },
      { value: "phd", label: "PhD" },
    ],
    validations: { required: true },
    size: 6,
  },
  {
    key: "institution_name",
    label: "Institution Name",
    type: FieldType.INPUT,
    placeholder: "Enter Institution Name",
    validations: { required: true },
    size: 6,
  },
  {
    key: "graduation_year",
    label: "Graduation Year",
    type: FieldType.INPUT,
    inputType: "number",
    placeholder: "Enter Graduation Year",
    validations: { required: true },
    size: 6,
  },
];

// ────────────────────────────────────────────────
// Step 4: Address
// ────────────────────────────────────────────────
const addressFields: FieldConfig[] = [
  {
    key: "address_line1",
    label: "Address Line 1",
    type: FieldType.INPUT,
    placeholder: "Enter Address Line 1",
    validations: { required: true },
    size: 12,
  },
  {
    key: "address_line2",
    label: "Address Line 2",
    type: FieldType.INPUT,
    placeholder: "Enter Address Line 2",
    size: 12,
  },
  {
    key: "city",
    label: "City",
    type: FieldType.INPUT,
    placeholder: "Enter City",
    validations: { required: true },
    size: 6,
  },
  {
    key: "state",
    label: "State/Province",
    type: FieldType.INPUT,
    placeholder: "Enter State or Province",
    validations: { required: true },
    size: 6,
  },
  {
    key: "postal_code",
    label: "Postal Code",
    type: FieldType.INPUT,
    placeholder: "Enter Postal Code",
    validations: { required: true },
    size: 6,
  },
  {
    key: "country",
    label: "Country",
    type: FieldType.SELECT,
    options: [
      { value: "Tanzania", label: "Tanzania" },
      { value: "USA", label: "USA" },
      { value: "UK", label: "UK" },
    ],
    validations: { required: true },
    size: 6,
  },
];

// ────────────────────────────────────────────────
// Step 5: Declaration
// ────────────────────────────────────────────────
const declarationFields: FieldConfig[] = [
  {
    key: "declaration",
    label:
      "I hereby declare that the information provided is true and correct.",
    type: FieldType.CHECKBOX,
    validations: { required: true },
    size: 12,
  },
  {
    key: "signature",
    label: "Signature",
    type: FieldType.INPUT,
    placeholder: "Type your full name as signature",
    validations: { required: true },
    size: 12,
  },
];

// ────────────────────────────────────────────────
// Combine steps with appropriate icons
// ────────────────────────────────────────────────
const steps: Step[] = [
  {
    title: "Particulars",
    icon: "person",
    fields: particularsFields,
    endpoint_create: "/user-profiles/create",
    endpoint_get: "/user-profiles/show",
    additionalValues: { user_id: 123, created_by: 123 },
    statusKey: "ClinicalTrial",
  },
  {
    title: "Passport",
    icon: "card_travel",
    fields: passportFields,
    endpoint_create: "/identity-cards/create",
    endpoint_get: "/identity-cards/show",
  },
  {
    title: "Education",
    icon: "school",
    fields: educationFields,
    endpoint_get: "",
    endpoint_create: "",
  },
  {
    title: "Address",
    icon: "home",
    fields: addressFields,
    endpoint_get: "",
    endpoint_create: "",
  },
  {
    title: "Declaration",
    icon: "gavel",
    fields: declarationFields,
    endpoint_get: "",
    endpoint_create: "",
  },
];

const MyMultiStepForm: React.FC = () => {
  const client = useClient("RESEARCHER_PROFILE_SERVICE");
  const userID = 2;
  const formStepsStatusEndpoint = "/research-profile-steps/list";
  const { stepStatus, loading } = useStepStatus(
    client,
    userID,
    formStepsStatusEndpoint
  );

  const handleFinalSubmit = (data: { [key: string]: any }) => {
    console.log("Final submitted data:", data);
  };

  const modifiedStepStatus = React.useMemo(() => {
    if (stepStatus && stepStatus.current_step.toLowerCase() === "completed") {
      return { ...stepStatus, current_step: "TrialOutcome" };
    }
    return stepStatus;
  }, [stepStatus]);

  if (loading) return <div>Loading...</div>;

  return (
    <StepperForm
      steps={steps}
      onSubmit={handleFinalSubmit}
      client={client}
      stepStatus={modifiedStepStatus || undefined}
    />
  );
};

export default MyMultiStepForm;
